import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AudioDevice } from "@/utils/audioSettings";
import React from "react";
import { AudioDeviceSelector } from "./AudioDeviceSelector";

interface AudioDeviceSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeviceSelected: (device: AudioDevice) => void;
  trigger?: React.ReactNode;
  mode?: "modal" | "popover";
  title?: string;
  description?: string;
}

export const AudioDeviceSetupModal: React.FC<AudioDeviceSetupModalProps> = ({
  isOpen,
  onClose,
  onDeviceSelected,
  trigger,
  mode = "modal",
  title = "Set up your microphone",
  description = "Before you can start recording, please select and test your microphone. This will ensure the best audio quality for your recordings.",
}) => {
  const handleDeviceSelected = (device: AudioDevice) => {
    onDeviceSelected(device);
    onClose();
  };

  const content = (
    <div className="max-w-md">
      <AudioDeviceSelector
        onDeviceSelected={handleDeviceSelected}
        onCancel={onClose}
        showCancelButton={true}
        className="border-0 shadow-none"
      />
    </div>
  );

  if (mode === "popover" && trigger) {
    return (
      <Popover open={isOpen} onOpenChange={onClose}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="w-96 p-0">
          <div className="p-4 space-y-2">
            <h3 className="font-semibold text-lg">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};
